<div [class]="compact() ? 'p-4' : 'container mx-auto px-4 py-8'">
  @if (!compact()) {
    <h1 class="text-3xl font-bold mb-8 text-gray-900 dark:text-white">Lookup</h1>
  }

  <div class="grid grid-cols-1 gap-6" [class.lg:grid-cols-2]="!compact()">
    <!-- Venue Lookup Section -->
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
      <h2 class="text-2xl font-semibold mb-4 text-gray-800 dark:text-white">Venue Lookup</h2>

      <div class="mb-4">
        <label for="venue-search" class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
          Search by Venue Name or ID
        </label>
        <div class="relative">
          <input
            type="text"
            id="venue-search"
            [(ngModel)]="venueSearchTerm"
            (input)="onVenueSearchChange()"
            placeholder="Enter venue name or ID..."
            class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-black focus:border-black block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-black dark:focus:border-black"
          />
          @if (venueSearchTerm) {
            <button
              (click)="clearVenueSearch()"
              class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          }
        </div>
      </div>

      <!-- Venue Results -->
      @if (selectedVenue$ | async; as venue) {
        <div class="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-700">
          <h3 class="text-xl font-semibold mb-3 text-gray-900 dark:text-white">{{ venue.name }}</h3>

          <div class="space-y-2 text-sm mb-4">
            <div>
              <span class="font-medium text-gray-700 dark:text-gray-300">Venue ID:</span>
              <span class="ml-2 text-gray-600 dark:text-gray-400">{{ venue.id }}</span>
            </div>

            @if (venue.description) {
              <div>
                <span class="font-medium text-gray-700 dark:text-gray-300">Description:</span>
                <span class="ml-2 text-gray-600 dark:text-gray-400">{{ venue.description }}</span>
              </div>
            }

            @if (venue.contactPerson) {
              <div>
                <span class="font-medium text-gray-700 dark:text-gray-300">Contact Person:</span>
                <span class="ml-2 text-gray-600 dark:text-gray-400">{{ venue.contactPerson }}</span>
              </div>
            }

            @if (venue.contactEmail) {
              <div>
                <span class="font-medium text-gray-700 dark:text-gray-300">Email:</span>
                <span class="ml-2 text-gray-600 dark:text-gray-400">{{ venue.contactEmail }}</span>
              </div>
            }

            @if (venue.contactPhone) {
              <div>
                <span class="font-medium text-gray-700 dark:text-gray-300">Phone:</span>
                <span class="ml-2 text-gray-600 dark:text-gray-400">{{ venue.contactPhone }}</span>
              </div>
            }

            @if (venue.address || venue.city || venue.state) {
              <div>
                <span class="font-medium text-gray-700 dark:text-gray-300">Address:</span>
                <span class="ml-2 text-gray-600 dark:text-gray-400">
                  {{ venue.address }}{{ venue.city ? ', ' + venue.city : '' }}{{ venue.state ? ', ' + venue.state : '' }}
                </span>
              </div>
            }

            <div>
              <span class="font-medium text-gray-700 dark:text-gray-300">Status:</span>
              <span class="ml-2 px-2 py-1 rounded text-xs font-semibold"
                    [ngClass]="venue.enabled ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'">
                {{ venue.enabled ? 'Enabled' : 'Disabled' }}
              </span>
            </div>

            @if (venue.subscriptionType && venue.subscriptionType !== 'None') {
              <div>
                <span class="font-medium text-gray-700 dark:text-gray-300">Subscription:</span>
                <span class="ml-2 text-gray-600 dark:text-gray-400">{{ venue.subscriptionType }} - ${{ venue.subscriptionAmount }}</span>
              </div>
            }

            @if (venue.presentmentCurrency) {
              <div>
                <span class="font-medium text-gray-700 dark:text-gray-300">Currency:</span>
                <span class="ml-2 text-gray-600 dark:text-gray-400">{{ venue.presentmentCurrency }}</span>
              </div>
            }
          </div>

          <button
            (click)="navigateToVenue(venue.id)"
            class="w-full px-4 py-2 text-sm font-medium text-white bg-black rounded-lg hover:bg-gray-800 focus:ring-4 focus:outline-none focus:ring-gray-300 dark:bg-gray-900 dark:hover:bg-gray-800 dark:focus:ring-gray-700"
          >
            View Full Details
          </button>
        </div>
      } @else if (venueSearchTerm) {
        <div class="text-center py-4 text-gray-500 dark:text-gray-400">
          No venue found matching "{{ venueSearchTerm }}"
        </div>
      }
    </div>

    <!-- User Lookup Section -->
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
      <h2 class="text-2xl font-semibold mb-4 text-gray-800 dark:text-white">User Lookup</h2>

      <div class="mb-4">
        <label for="user-search" class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
          Search by User ID or Email
        </label>
        <div class="relative mb-4">
          <input
            type="text"
            id="user-search"
            [(ngModel)]="userSearchTerm"
            (keyup.enter)="onUserSearch()"
            placeholder="Enter user ID or email..."
            class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-black focus:border-black block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-black dark:focus:border-black"
          />
          @if (userSearchTerm) {
            <button
              (click)="clearUserSearch()"
              class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          }
        </div>

        <button
          (click)="onUserSearch()"
          [disabled]="!userSearchTerm.trim() || isLoadingUser"
          class="w-full px-4 py-2 text-sm font-medium text-white bg-black rounded-lg hover:bg-gray-800 focus:ring-4 focus:outline-none focus:ring-gray-300 disabled:bg-gray-400 disabled:cursor-not-allowed dark:bg-gray-900 dark:hover:bg-gray-800 dark:focus:ring-gray-700 dark:disabled:bg-gray-600"
        >
          @if (isLoadingUser) {
            <span class="flex items-center justify-center">
              <svg class="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Searching...
            </span>
          } @else {
            Search User
          }
        </button>
      </div>

      <!-- Loading State -->
      @if (isLoadingUser) {
        <div class="flex items-center justify-center py-8">
          <svg class="animate-spin h-10 w-10 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      }

      <!-- Error State -->
      @if (userError && !isLoadingUser) {
        <div class="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-800 rounded-lg p-4 text-sm text-red-800 dark:text-red-300">
          <div class="flex items-start">
            <svg class="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path>
            </svg>
            <div>
              <p class="font-medium">Error</p>
              <p class="mt-1">{{ userError }}</p>
            </div>
          </div>
        </div>
      }

      <!-- User Results -->
      @if (!isLoadingUser) {
        <div class="space-y-4">

          @if (userHistory) {
            <!-- Profile Summary -->
            <div class="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-700">
              <h3 class="text-xl font-semibold mb-3 text-gray-900 dark:text-white">Profile</h3>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                @if (getUserDisplayName()) {
                  <div>
                    <span class="text-xs text-gray-500 dark:text-gray-400 block">Name</span>
                    <span class="font-semibold text-gray-900 dark:text-white">{{ getUserDisplayName() }}</span>
                  </div>
                }
                <div>
                  <span class="text-xs text-gray-500 dark:text-gray-400 block">Account Type</span>
                  <span class="text-gray-900 dark:text-white">
                    @if (userHistory.systemUser && userHistory.user) {
                      Consumer + Staff
                    } @else if (userHistory.systemUser) {
                      Staff
                    } @else {
                      Consumer
                    }
                  </span>
                </div>
                @if (userHistory.systemUser?.email) {
                  <div>
                    <span class="text-xs text-gray-500 dark:text-gray-400 block">Email</span>
                    <span class="text-gray-900 dark:text-white break-all">{{ userHistory.systemUser?.email }}</span>
                  </div>
                }
              </div>
            </div>

            <!-- Venue Memberships (venues/{id}.users contains this uid) -->
            @if (getMemberVenues().length > 0) {
              <div class="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-700">
                <h3 class="text-xl font-semibold mb-3 text-gray-900 dark:text-white">Venue Memberships</h3>
                <div class="space-y-2">
                  @for (venue of getMemberVenues(); track venue.venueID) {
                    <button
                      type="button"
                      (click)="navigateToVenue(venue.venueID)"
                      class="w-full flex justify-between items-center py-2 px-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg text-sm hover:bg-gray-100 dark:hover:bg-gray-600 text-left"
                    >
                      <span class="font-medium text-gray-900 dark:text-white truncate mr-2">{{ venue.venueName }}</span>
                      <span class="font-mono text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">{{ venue.venueID }}</span>
                    </button>
                  }
                </div>
              </div>
            }

            <div class="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-700">
              <h3 class="text-xl font-semibold mb-3 text-gray-900 dark:text-white">User History</h3>

              <pre class="bg-gray-900 dark:bg-gray-950 text-green-400 p-4 rounded-lg overflow-x-auto text-xs font-mono whitespace-pre-wrap break-words">{{ formatJson(userHistory) }}</pre>
            </div>
          }
        </div>
      } @else if (userSearchPerformed && !isLoadingUser && !userError) {
        <div class="text-center py-4 text-gray-500 dark:text-gray-400">
          No user found matching "{{ userSearchTerm }}"
        </div>
      } @else if (!isLoadingUser && !userError) {
        <div class="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
          <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
          </svg>
          <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">Enter a user ID or email to search</p>
        </div>
      }
    </div>
  </div>
</div>
